// useCycleData.js
import { useState, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import axios from "axios";
import { API_URL } from "../../../../constants";

dayjs.extend(isBetween);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function parseDate(raw) {
  if (raw == null) return null;
  if (dayjs.isDayjs(raw)) return raw.startOf("day");
  if (raw instanceof Date) return dayjs(raw).startOf("day");
  if (typeof raw === "string") {
    const parsed = dayjs(raw.split("T")[0]);
    return parsed.isValid() ? parsed.startOf("day") : null;
  }
  return null;
}

export function parseCycle(c) {
  return {
    ...c,
    cycle_start_date:      parseDate(c.cycle_start_date),
    menstrual_start_date:  parseDate(c.menstrual_start_date),
    menstrual_end_date:    parseDate(c.menstrual_end_date),
    follicular_start_date: parseDate(c.follicular_start_date),
    follicular_end_date:   parseDate(c.follicular_end_date),
    ovulation_start_date:  parseDate(c.ovulation_start_date),
    ovulation_end_date:    parseDate(c.ovulation_end_date),
    luteal_start_date:     parseDate(c.luteal_start_date),
    luteal_end_date:       parseDate(c.luteal_end_date),
  };
}

/** Parse a raw symptom object — normalise the date field. */
export function parseSymptom(s) {
  return { ...s, date: parseDate(s.date) };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export default function useCycleData() {
  // Real cycles
  const [cycles, setCycles]               = useState([]);
  // Predicted future cycles
  const [predictedCycles, setPredictedCycles] = useState([]);
  // All symptoms across all cycles
  const [symptoms, setSymptoms]           = useState([]);
  /**
   * symptomsByDate: Map<"YYYY-MM-DD", symptom[]>
   * Pre-built for O(1) calendar dot lookups.
   */
  const [symptomsByDate, setSymptomsByDate] = useState({});

  const [activeCycle, setActiveCycle]     = useState(null);
  const [statistics, setStatistics]       = useState(null);
  const [loading, setLoading]             = useState(true);
  const [symptomsLoading, setSymptomsLoading] = useState(false);

  const [selectedDate, setSelectedDate]   = useState(null);
  const [snackbar, setSnackbar]           = useState({ open: false, message: "", severity: "success" });

  // Dialogs
  const [symptomDialogOpen, setSymptomDialogOpen]           = useState(false);
  const [symptomDialogMode, setSymptomDialogMode]           = useState("add");   // "add" | "view"
  const [symptomDialogDate, setSymptomDialogDate]           = useState(null);    // dayjs date shown in dialog

  const [updateDialogOpen, setUpdateDialogOpen]             = useState(false);
  const [updateForm, setUpdateForm]                         = useState({});

  const [endCycleDialogOpen, setEndCycleDialogOpen]         = useState(false);
  const [endCycleForm, setEndCycleForm]                     = useState({ next_cycle_start_date: null });

  const [adjustPhaseDialogOpen, setAdjustPhaseDialogOpen]   = useState(false);
  const [adjustPhaseTargetCycle, setAdjustPhaseTargetCycle] = useState(null);
  const [adjustPhaseForm, setAdjustPhaseForm]               = useState({
    menstrual_end_date: null, ovulation_start_date: null, ovulation_end_date: null,
  });

  const [historyEditRow, setHistoryEditRow]   = useState(null);
  const [historyEditForm, setHistoryEditForm] = useState({});

  // ─────────────────────────────────────────────────────────────────────────
  // Data loading
  // ─────────────────────────────────────────────────────────────────────────

  const loadSymptoms = useCallback(async () => {
    setSymptomsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/symptoms/`);
      const parsed = (res.data || []).map(parseSymptom);
      setSymptoms(parsed);

      // Build date → symptom[] lookup
      const byDate = {};
      parsed.forEach((s) => {
        const key = s.date?.format("YYYY-MM-DD");
        if (!key) return;
        if (!byDate[key]) byDate[key] = [];
        byDate[key].push(s);
      });
      setSymptomsByDate(byDate);
    } catch (err) {
      console.error("Failed to load symptoms", err);
    } finally {
      setSymptomsLoading(false);
    }
  }, []);

  const loadCycles = useCallback(async () => {
    setLoading(true);
    try {
      const [cyclesRes, upcomingRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/cycles/`),
        axios.get(`${API_URL}/api/cycles/upcoming/`),
        axios.get(`${API_URL}/api/cycles/statistics/`),
      ]);

      const realParsed = (cyclesRes.data || [])
        .map(parseCycle)
        .sort((a, b) => (b.cycle_start_date?.valueOf() || 0) - (a.cycle_start_date?.valueOf() || 0));

      const predictedParsed = (upcomingRes.data || [])
        .map(parseCycle)
        .sort((a, b) => (a.cycle_start_date?.valueOf() || 0) - (b.cycle_start_date?.valueOf() || 0));

      setCycles(realParsed);
      setPredictedCycles(predictedParsed);
      setActiveCycle(realParsed.find((c) => c.is_active) || null);
      setStatistics(statsRes.data);
    } catch (err) {
      console.error("Failed to load cycles", err);
      notify("Failed to load cycle data", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAll = useCallback(async () => {
    await Promise.all([loadCycles(), loadSymptoms()]);
  }, [loadCycles, loadSymptoms]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ─────────────────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────────────────

  const notify = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  /** Returns { phase: string, isPredicted: boolean } | null */
  const getPhaseForDate = useCallback(
    (date) => {
      if (!date) return null;
      const check = (c) => {
        if (c.menstrual_start_date && c.menstrual_end_date &&
            !date.isBefore(c.menstrual_start_date) && !date.isAfter(c.menstrual_end_date))
          return { phase: "menstrual", isPredicted: !!c.is_system_prediction };
        if (c.follicular_start_date && c.follicular_end_date &&
            !date.isBefore(c.follicular_start_date) && !date.isAfter(c.follicular_end_date))
          return { phase: "follicular", isPredicted: !!c.is_system_prediction };
        if (c.ovulation_start_date && c.ovulation_end_date &&
            !date.isBefore(c.ovulation_start_date) && !date.isAfter(c.ovulation_end_date))
          return { phase: "ovulation", isPredicted: !!c.is_system_prediction };
        if (c.luteal_start_date && c.luteal_end_date &&
            !date.isBefore(c.luteal_start_date) && !date.isAfter(c.luteal_end_date))
          return { phase: "luteal", isPredicted: !!c.is_system_prediction };
        return null;
      };
      for (const c of cycles) { const r = check(c); if (r) return r; }
      for (const c of predictedCycles) { const r = check(c); if (r) return r; }
      return null;
    },
    [cycles, predictedCycles]
  );

  /** Returns the real cycle containing the given date (for symptom logging). */
  const getCycleForDate = useCallback(
    (date) => {
      if (!date) return null;
      return cycles.find(
        (c) => c.cycle_start_date && c.luteal_end_date &&
               !date.isBefore(c.cycle_start_date) && !date.isAfter(c.luteal_end_date)
      ) || null;
    },
    [cycles]
  );

  /** Returns symptom[] for a given "YYYY-MM-DD" string key. */
  const getSymptomsForDate = useCallback(
    (dateKey) => symptomsByDate[dateKey] || [],
    [symptomsByDate]
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Calendar day click — routes to "view" or "add" mode
  // ─────────────────────────────────────────────────────────────────────────

  const handleCalendarDayClick = useCallback(
    (date) => {
      const d = dayjs(date);
      const phaseResult = getPhaseForDate(d);

      // Predicted days are read-only
      if (phaseResult?.isPredicted) return;

      const cycle = getCycleForDate(d);
      if (!cycle) return;

      const dateKey = d.format("YYYY-MM-DD");
      const existing = symptomsByDate[dateKey] || [];

      setSymptomDialogDate(d);
      setSelectedDate(d);
      // If the day already has symptoms, open in "view" mode first
      setSymptomDialogMode(existing.length > 0 ? "view" : "add");
      setSymptomDialogOpen(true);
    },
    [getPhaseForDate, getCycleForDate, symptomsByDate]
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Symptom CRUD
  // ─────────────────────────────────────────────────────────────────────────

  const submitSymptom = async (formValues) => {
    try {
      await axios.post(`${API_URL}/api/symptoms/`, formValues);
      notify("Symptom logged");
      await loadSymptoms(); // only reload symptoms, not full cycle data
    } catch (err) {
      notify(err.response?.data?.detail || "Failed to log symptom", "error");
      throw err; // let form reset logic handle it
    }
  };

  const updateSymptom = async (id, patch) => {
    try {
      await axios.patch(`${API_URL}/api/symptoms/${id}/`, patch);
      notify("Symptom updated");
      await loadSymptoms();
    } catch (err) {
      notify(err.response?.data?.detail || "Failed to update symptom", "error");
    }
  };

  const deleteSymptom = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/symptoms/${id}/`);
      notify("Symptom deleted");
      await loadSymptoms();
    } catch (err) {
      notify(err.response?.data?.detail || "Failed to delete symptom", "error");
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Update active cycle
  // ─────────────────────────────────────────────────────────────────────────

  const openUpdateDialog = () => {
    if (!activeCycle) return;
    setUpdateForm({
      cycle_start_date: activeCycle.cycle_start_date,
      period_length:    activeCycle.period_length,
      notes:            activeCycle.notes || "",
    });
    setUpdateDialogOpen(true);
  };

  const submitUpdateCycle = async () => {
    if (!activeCycle) return;
    try {
      await axios.patch(`${API_URL}/api/cycles/${activeCycle.id}/`, {
        cycle_start_date: updateForm.cycle_start_date?.format("YYYY-MM-DD"),
        period_length:    updateForm.period_length,
        notes:            updateForm.notes,
      });
      setUpdateDialogOpen(false);
      notify("Cycle updated");
      await loadCycles();
    } catch (err) {
      notify(err.response?.data?.detail || "Failed to update cycle", "error");
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // End cycle
  // ─────────────────────────────────────────────────────────────────────────

  const openEndCycleDialog = () => {
    setEndCycleForm({ next_cycle_start_date: null });
    setEndCycleDialogOpen(true);
  };

  const submitEndCycle = async () => {
    if (!activeCycle || !endCycleForm.next_cycle_start_date) return;
    try {
      await axios.post(`${API_URL}/api/cycles/${activeCycle.id}/end-cycle/`, {
        next_cycle_start_date: endCycleForm.next_cycle_start_date.format("YYYY-MM-DD"),
      });
      setEndCycleDialogOpen(false);
      notify("Cycle ended");
      await loadCycles();
    } catch (err) {
      notify(err.response?.data?.detail || "Failed to end cycle", "error");
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Phase boundary adjustment
  // ─────────────────────────────────────────────────────────────────────────

  const openAdjustPhaseDialog = (cycle) => {
    setAdjustPhaseTargetCycle(cycle);
    setAdjustPhaseForm({
      menstrual_end_date:   cycle.menstrual_end_date,
      ovulation_start_date: cycle.ovulation_start_date,
      ovulation_end_date:   cycle.ovulation_end_date,
    });
    setAdjustPhaseDialogOpen(true);
  };

  const submitAdjustPhases = async () => {
    if (!adjustPhaseTargetCycle) return;
    const payload = {};
    if (adjustPhaseForm.menstrual_end_date)
      payload.menstrual_end_date = adjustPhaseForm.menstrual_end_date.format("YYYY-MM-DD");
    if (adjustPhaseForm.ovulation_start_date)
      payload.ovulation_start_date = adjustPhaseForm.ovulation_start_date.format("YYYY-MM-DD");
    if (adjustPhaseForm.ovulation_end_date)
      payload.ovulation_end_date = adjustPhaseForm.ovulation_end_date.format("YYYY-MM-DD");
    try {
      await axios.patch(
        `${API_URL}/api/cycles/${adjustPhaseTargetCycle.id}/adjust-phases/`,
        payload
      );
      setAdjustPhaseDialogOpen(false);
      notify("Phase boundaries updated");
      await loadCycles();
    } catch (err) {
      notify(err.response?.data?.detail || "Failed to adjust phases", "error");
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // History table inline editing
  // ─────────────────────────────────────────────────────────────────────────

  const startEditRow = (cycle) => {
    setHistoryEditRow(cycle.id);
    setHistoryEditForm({
      period_length:       cycle.period_length,
      menstrual_symptoms:  cycle.menstrual_symptoms  || "",
      follicular_symptoms: cycle.follicular_symptoms || "",
      ovulation_symptoms:  cycle.ovulation_symptoms  || "",
      luteal_symptoms:     cycle.luteal_symptoms     || "",
      notes:               cycle.notes               || "",
    });
  };

  const cancelEditRow = () => { setHistoryEditRow(null); setHistoryEditForm({}); };

  const saveEditRow = async (cycleId) => {
    try {
      await axios.patch(`${API_URL}/api/cycles/${cycleId}/`, historyEditForm);
      setHistoryEditRow(null);
      notify("Cycle saved");
      await loadCycles();
    } catch (err) {
      notify(err.response?.data?.detail || "Failed to save cycle", "error");
    }
  };

  const deleteCycle = async (cycleId) => {
    try {
      await axios.delete(`${API_URL}/api/cycles/${cycleId}/`);
      notify("Cycle deleted");
      await loadAll();
    } catch (err) {
      notify(err.response?.data?.detail || "Failed to delete cycle", "error");
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Exposed API
  // ─────────────────────────────────────────────────────────────────────────

  return {
    // Data
    cycles, predictedCycles, activeCycle, statistics,
    symptoms, symptomsByDate, symptomsLoading,
    loading, selectedDate, setSelectedDate,

    // Utilities
    getPhaseForDate, getCycleForDate, getSymptomsForDate,
    loadCycles, loadSymptoms, loadAll,

    // Snackbar
    snackbar, setSnackbar,

    // Calendar day click (unified entry point)
    handleCalendarDayClick,

    // Symptom dialog state
    symptomDialogOpen, setSymptomDialogOpen,
    symptomDialogMode, setSymptomDialogMode,
    symptomDialogDate, setSymptomDialogDate,

    // Symptom CRUD
    submitSymptom, updateSymptom, deleteSymptom,

    // Update cycle
    updateDialogOpen, setUpdateDialogOpen, updateForm, setUpdateForm,
    openUpdateDialog, submitUpdateCycle,

    // End cycle
    endCycleDialogOpen, setEndCycleDialogOpen, endCycleForm, setEndCycleForm,
    openEndCycleDialog, submitEndCycle,

    // Phase adjustment
    adjustPhaseDialogOpen, setAdjustPhaseDialogOpen,
    adjustPhaseTargetCycle, adjustPhaseForm, setAdjustPhaseForm,
    openAdjustPhaseDialog, submitAdjustPhases,

    // History table editing
    historyEditRow, historyEditForm, setHistoryEditForm,
    startEditRow, cancelEditRow, saveEditRow, deleteCycle,
  };
}